import React__default, { forwardRef } from 'react';
import { Search, Close } from '@livingdesign/icons';
import { applyCommonProps } from '../utility/commonProps.js';
import '@walmart-web/livingdesign-components/index.esm.css';
import '@walmart-web/livingdesign-components';
import '../Alert/Alert.js';
import '../Button/Button.js';
import '../Button/ButtonGroup.js';
import '../Radio/Radio.js';
import '../Tag/Tag.js';
import '../Checkbox/Checkbox.js';
import '../Chip/Chip.js';
import '../../node_modules/classnames/index.js';
import '../IconButton/IconButton.js';
import '@livingdesign/tokens/dist/js/light/regular/components/Form';
import { TextField } from '../TextField/TextField.js';
import '../TabNavigation/TabNavigation.js';
import '../TabNavigation/TabNavigationItem.js';
import '../Modal/Modal.js';
import '@livingdesign/tokens/dist/js/light/regular/components/ErrorMessage';
import '../AutoComplete/AutoComplete.js';
import '../TextArea/TextArea.js';
import '../ProgressIndicator/ProgressIndicator.js';
import '../Panel/Panel.js';
import '../Calendar/locale/wmLocale.js';
import '../Calendar/locale/stdLocale.js';
import '../Calendar/context.js';
import 'dayjs';
import '../Calendar/RenderDays.js';
import '../Calendar/RenderWeeks.js';
import '../utility/constants.js';
import '@react-aria/ssr';
import '../CardActionArea/CardActionArea.js';
import '../HierarchySelector/HierarchySelector.js';
import '../Pagination/Pagination.js';
import '../DisplayTag/DisplayTag.js';
import '../PageHeader/PageHeader.js';
import '../Table/Table.js';
import '../Tooltip/Tooltip.js';
import '../ValueChange/ValueChange.js';
import styles from './SearchBox.module.scss.js';

/**
 * Search box allow users to search text.
 *
 * {@link https://livingdesign.walmart.com/components/text-field/ Guidelines}
 * {@link https://livingdesign.walmart.com/components/text-field/react/ React documentation}
 *
 * ### Content
 *
 * #### Label
 *
 * Search Box requires a label to be accessible. Use the `label` property to add a label:
 *
 * ```tsx
 * <SearchBox
 *   label="Search"
 *   onChange={(event) => console.log(event)}
 * />
 * ```
 *
 * #### Leading and trailing
 *
 * Clarify the Seach Box's purpose by adding an icon with the `leadingIcon` prop:
 *
 * ```tsx
 * import React from "react";
 * import { Search } from '@livingdesign/icons';
 *
 * export default () => {
 *   return (
 *     <SearchBox
 *       label="search"
 *       leadingIcon={<Search size="small" />}
 *       onChange={(event) => console.log(event)}
 *     />
 *   );
 * };
 * ```
 *
 * Enhance Search Box's functionality by passing additional content to the `trailing` slot:
 *
 * ```tsx
 * import React from "react";
 * import { Close } from '@livingdesign/icons';
 *
 * export default () => {
 *   return (
 *     <SearchBox
 *       label="Search"
 *       onChange={(event) => console.log(event)}
 *       trailing={<Close size="small" />}
 *     />
 *   );
 * };
 * ```
 *
 * ### Interaction
 *
 * Configure the Search Box's value using the `value` property. Respond to changes by passing an callback handler to the `onChange` prop:
 *
 * ```tsx
 * import * as React from "react";
 *
 * export default () => {
 *   const [value, setValue] = useState("CA");
 *
 *   return (
 *     <SearchBox
 *       label="Full name"
 *       onChange={(event) => setValue(event.target.value)}
 *       value={value}
 *     />
 *   );
 * };
 * ```
 */

var SearchBox = /*#__PURE__*/forwardRef(function (props, ref) {
  var _applyCommonProps = applyCommonProps(props, SearchBox),
    label = _applyCommonProps.label,
    _applyCommonProps$val = _applyCommonProps.value,
    value = _applyCommonProps$val === void 0 ? '' : _applyCommonProps$val,
    onChange = _applyCommonProps.onChange,
    error = _applyCommonProps.error,
    placeholder = _applyCommonProps.placeholder,
    _applyCommonProps$rea = _applyCommonProps.readOnly,
    readOnly = _applyCommonProps$rea === void 0 ? false : _applyCommonProps$rea,
    helperText = _applyCommonProps.helperText,
    _applyCommonProps$dis = _applyCommonProps.disabled,
    disabled = _applyCommonProps$dis === void 0 ? false : _applyCommonProps$dis,
    className = _applyCommonProps.className,
    size = _applyCommonProps.size,
    onKeyDown = _applyCommonProps.onKeyDown,
    onCloseIconClick = _applyCommonProps.onCloseIconClick;
  var handleCloseIconClick = function handleCloseIconClick() {
    onCloseIconClick();
  };
  return /*#__PURE__*/React__default.createElement(TextField, {
    label: label,
    leadingIcon: /*#__PURE__*/React__default.createElement(Search, {
      size: "small"
    }),
    trailing: value ? /*#__PURE__*/React__default.createElement(Close, {
      size: "small",
      onClick: handleCloseIconClick,
      className: styles.closeIconAlign
    }) : '',
    onChange: onChange,
    size: size,
    type: "text",
    UNSAFE_className: className,
    placeholder: placeholder,
    readOnly: readOnly,
    helperText: helperText,
    disabled: disabled,
    value: value,
    error: error,
    ref: ref,
    onKeyDown: onKeyDown,
    "data-testid": "search-text-box"
  });
});
SearchBox.displayName = 'SearchBox';

export { SearchBox };
